/**
 * Portfolio financials: the questions a single project's page cannot answer.
 *
 * Project financials tell you how one deal is doing. This tells you where in the portfolio the
 * money is leaking - which projects are past their cost plan, what the whole book has committed
 * and earned, and how any two dimensions compare across every project at once. Everything here
 * clicks through to the project that owns the number, because that is where it gets fixed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  Buildings, TrendUp, Warning, ArrowSquareOut, Coins, ChartPieSlice,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { PivotMatrix } from '../../components/admin/finance/PivotMatrix';
import { money, percent } from '../../components/admin/finance/financeFormat';

const BASIS_OPTIONS = [
  { key: 'split', label: 'Cost + revenue' },
  { key: 'net', label: 'Net' },
  { key: 'costs', label: 'Costs' },
  { key: 'income', label: 'Revenue' },
];

const KpiCard = ({ icon: Icon, chip, label, hint, budget, actual, extra, testId }) => (
  <Card className="bg-white p-4 rounded-xl border border-black/[0.04]" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <span className={cn('p-1.5 rounded-lg', chip)}>
        <Icon className="h-4 w-4" weight="duotone" />
      </span>
      <div>
        <p className="text-sm font-medium leading-tight">{label}</p>
        <p className="text-[11px] text-foreground-muted">{hint}</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-foreground-muted">Planned</p>
        <p className="text-lg font-bold tabular-nums">{money(budget)}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-foreground-muted">Actual</p>
        <p className="text-lg font-bold tabular-nums">{money(actual)}</p>
      </div>
    </div>
    {extra && <div className="mt-2 pt-2 border-t border-black/[0.04] text-xs text-foreground-muted">{extra}</div>}
  </Card>
);

export default function AdminFinanceReportsPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pivot, setPivot] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(true);
  const [config, setConfig] = useState({
    rows: 'project',
    columns: 'phase',
    measures: ['budget', 'actual', 'variance'],
    basis: 'split',
  });
  const [dimensions, setDimensions] = useState([]);

  useEffect(() => {
    api.get('/admin/v2/finance/portfolio/summary')
      .then((res) => setSummary(res.data))
      .catch(() => toast.error('Could not load the portfolio summary'))
      .finally(() => setLoading(false));
    api.get('/admin/v2/finance/reports/dimensions')
      .then((res) => setDimensions(res.data.dimensions || []))
      .catch(() => {});
  }, []);

  const loadPivot = useCallback(async () => {
    setPivotLoading(true);
    try {
      const res = await api.post('/admin/v2/finance/portfolio/pivot', config);
      setPivot(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not build that pivot');
    } finally {
      setPivotLoading(false);
    }
  }, [config]);

  useEffect(() => { loadPivot(); }, [loadPivot]);

  const totals = summary?.totals;
  const projects = summary?.projects || [];

  return (
    <div className="space-y-6" data-testid="portfolio-financials-page">
      <header>
        <h1 className="text-2xl font-semibold">Portfolio financials</h1>
        <p className="text-sm text-foreground-muted mt-1">
          Every project's money in one place. Open a project to change anything.
        </p>
      </header>

      {loading ? (
        <p className="py-16 text-center text-sm text-foreground-muted">Loading the portfolio…</p>
      ) : !totals || totals.projects_count === 0 ? (
        <Card className="bg-white p-10 rounded-xl border border-black/[0.04] text-center" data-testid="portfolio-empty">
          <p className="text-sm text-foreground-muted">
            No project has finance entries yet. Budgets and transactions are recorded on a project's
            own Financials tab.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard
              icon={Coins}
              chip="bg-blue-50 text-blue-700"
              label="Total CAPEX"
              hint="Acquisition + development, all projects"
              budget={totals.capex.budget}
              actual={totals.capex.actual}
              testId="portfolio-kpi-capex"
            />
            <KpiCard
              icon={TrendUp}
              chip="bg-emerald-50 text-emerald-700"
              label="Revenue"
              hint="Rent and exit proceeds"
              budget={totals.income.budget}
              actual={totals.income.actual}
              testId="portfolio-kpi-revenue"
            />
            <KpiCard
              icon={ChartPieSlice}
              chip="bg-purple-50 text-purple-700"
              label="Net"
              hint="Revenue less all costs"
              budget={totals.net.budget}
              actual={totals.net.actual}
              extra={
                <>
                  {totals.projects_count} project{totals.projects_count === 1 ? '' : 's'} with
                  finance data · costs {money(totals.costs.actual)} of {money(totals.costs.budget)}{' '}
                  planned
                </>
              }
              testId="portfolio-kpi-net"
            />
          </div>

          {totals.over_budget_count > 0 && (
            <div
              className="flex items-center gap-2 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900"
              data-testid="portfolio-over-budget-banner"
            >
              <Warning className="h-4 w-4" weight="duotone" />
              {totals.over_budget_count} project{totals.over_budget_count === 1 ? ' is' : 's are'}{' '}
              past their cost plan, by {money(totals.over_budget_amount)} in total.
            </div>
          )}

          {/* League table */}
          <Card className="bg-white rounded-xl border border-black/[0.04] overflow-hidden">
            <header className="px-4 py-3 border-b border-black/[0.05]">
              <h2 className="text-sm font-semibold">By project</h2>
              <p className="text-xs text-foreground-muted mt-0.5">
                Biggest spend first. Click a row to open its financials.
              </p>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]" data-testid="portfolio-league-table">
                <thead>
                  <tr className="border-b border-black/[0.08]">
                    {['Project', 'Cost plan', 'Cost actual', 'Used', 'Revenue plan', 'Revenue actual', 'Net actual', ''].map((h, i) => (
                      <th
                        key={`${h}-${i}`}
                        className={cn(
                          'py-2 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-foreground-muted',
                          i === 0 ? 'text-left pl-4' : 'text-right',
                          i === 7 && 'pr-4'
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr
                      key={project.project_id}
                      className="border-b border-black/[0.04] hover:bg-black/[0.015] cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/v2/projects/${project.project_id}`)}
                      data-testid={`portfolio-row-${project.code}`}
                    >
                      <td className="py-2.5 pl-4 px-3">
                        <div className="flex items-center gap-2">
                          <Buildings className="h-4 w-4 text-foreground-muted" />
                          <div>
                            <p className="text-sm font-medium">{project.title}</p>
                            <p className="text-xs text-foreground-muted">
                              {project.code}
                              {project.status ? ` · ${project.status}` : ''}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm tabular-nums text-foreground-muted">
                        {money(project.costs.budget)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm tabular-nums font-medium">
                        {money(project.costs.actual)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm tabular-nums">
                        {project.cost_used_percent === null ? (
                          <span className="text-foreground-muted/50">·</span>
                        ) : (
                          <span className={cn(project.over_budget ? 'text-red-600 font-medium' : 'text-foreground-muted')}>
                            {percent(project.cost_used_percent, 0)}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm tabular-nums text-foreground-muted">
                        {project.income.budget ? money(project.income.budget) : <span className="text-foreground-muted/50">·</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right text-sm tabular-nums text-emerald-600">
                        {project.income.actual ? money(project.income.actual) : <span className="text-foreground-muted/50">·</span>}
                      </td>
                      <td className={cn('py-2.5 px-3 text-right text-sm tabular-nums font-medium', Number(project.net.actual) >= 0 ? 'text-emerald-600' : 'text-foreground')}>
                        {money(project.net.actual)}
                      </td>
                      <td className="py-2.5 pr-4 px-3 text-right">
                        <ArrowSquareOut className="h-4 w-4 text-foreground-muted inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-black/[0.03] font-semibold" data-testid="portfolio-league-totals">
                    <td className="py-3 pl-4 px-3 text-sm">Portfolio</td>
                    <td className="py-3 px-3 text-right text-sm tabular-nums">{money(totals.costs.budget)}</td>
                    <td className="py-3 px-3 text-right text-sm tabular-nums">{money(totals.costs.actual)}</td>
                    <td className="py-3 px-3" />
                    <td className="py-3 px-3 text-right text-sm tabular-nums">{money(totals.income.budget)}</td>
                    <td className="py-3 px-3 text-right text-sm tabular-nums">{money(totals.income.actual)}</td>
                    <td className="py-3 px-3 text-right text-sm tabular-nums">{money(totals.net.actual)}</td>
                    <td className="py-3 pr-4 px-3" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Cross-project pivot */}
          <Card className="bg-white rounded-xl border border-black/[0.04] overflow-hidden">
            <header className="flex flex-wrap items-end justify-between gap-3 px-4 py-3 border-b border-black/[0.05]">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-40">
                  <Label className="text-xs">Rows</Label>
                  <Select value={config.rows} onValueChange={(value) => setConfig((p) => ({ ...p, rows: value }))}>
                    <SelectTrigger className="h-9" data-testid="portfolio-pivot-rows">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dimensions.filter((d) => d.value !== 'none').map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-40">
                  <Label className="text-xs">Columns</Label>
                  <Select value={config.columns} onValueChange={(value) => setConfig((p) => ({ ...p, columns: value }))}>
                    <SelectTrigger className="h-9" data-testid="portfolio-pivot-columns">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dimensions.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-1 p-1 rounded-lg bg-black/[0.04]" data-testid="portfolio-pivot-basis">
                {BASIS_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => setConfig((p) => ({ ...p, basis: option.key }))}
                    className={cn(
                      'px-2.5 py-1 text-xs rounded-md transition-colors whitespace-nowrap',
                      config.basis === option.key ? 'bg-white shadow-sm font-medium' : 'text-foreground-muted'
                    )}
                    data-testid={`portfolio-basis-${option.key}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </header>
            {pivotLoading ? (
              <p className="py-12 text-center text-sm text-foreground-muted">Building the matrix…</p>
            ) : (
              <PivotMatrix pivot={pivot} />
            )}
            <footer className="px-4 py-3 border-t border-black/[0.05]">
              <p className="text-xs text-foreground-muted">
                Across every project. Per-project exports, the journal and budget revisions live on
                each project's own Financials tab.
              </p>
            </footer>
          </Card>
        </>
      )}
    </div>
  );
}
